import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { submitNewPassword } from '../services/passwordResetService';
// Same shared auth-card stylesheet as LoginPage and ForgotPasswordPage - see the note there.
import styles from './LoginPage.module.css';

// Mirrors the backend policy (passwordResetService.MIN_PASSWORD) and the registration form.
// Checking it here is a courtesy that saves a round trip; the backend check is the real one,
// because anything enforced only in a browser is not enforced.
const MIN_PASSWORD = 8;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // The token arrives in the URL and stays there - it is single-use and short-lived, and it
  // is already in the browser's history the moment the user clicks the emailed link. Copying
  // it into component state would not remove it from anywhere it already is.
  const token = useMemo(() => params.get('token') || '', [params]);

  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [done,     setDone]     = useState(false);
  const [dead,     setDead]     = useState(false); // link expired or already spent
  const [logoOk,   setLogoOk]   = useState(true);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD;
  const mismatch = confirm.length > 0 && password !== confirm;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (password.length < MIN_PASSWORD) return setError(`Password must be at least ${MIN_PASSWORD} characters.`);
    if (password !== confirm)           return setError('Passwords do not match.');

    setLoading(true);
    const result = await submitNewPassword(token, password);
    setLoading(false);

    if (result.ok) {
      setDone(true);
      return;
    }

    setError(result.message);
    // A dead link is not something the user can fix by retrying, so swap the form for the
    // one action that helps: ask for a fresh link.
    if (result.expired || result.invalid) setDead(true);
  }

  function renderBody() {
    // No token at all - someone opened /reset-password directly, or a mail client mangled
    // the URL. Say what to do rather than showing a form that cannot succeed.
    if (!token) {
      return (
        <>
          <p className={styles.tagline}>Reset link not recognised</p>
          <div className={styles.error} role="alert">
            This page needs a reset link from your email. Request a new one and open the link
            from the message we send.
          </div>
          <button className={styles.btn} type="button" onClick={() => navigate('/forgot-password')}>
            Request a new link
          </button>
        </>
      );
    }

    if (done) {
      return (
        <>
          <p className={styles.tagline}>Password updated</p>
          <div className={styles.info} role="status">
            Your password has been reset and you've been signed out everywhere. Sign in with
            your new password to continue.
          </div>
          <button className={styles.btn} type="button" onClick={() => navigate('/login', { replace: true })}>
            Go to sign in
          </button>
        </>
      );
    }

    if (dead) {
      return (
        <>
          <p className={styles.tagline}>This link no longer works</p>
          <div className={styles.error} role="alert">{error}</div>
          <p className={styles.helper}>
            Reset links expire an hour after they're sent and stop working once they've been
            used. Requesting a new one takes a moment.
          </p>
          <button className={styles.btn} type="button" onClick={() => navigate('/forgot-password')}>
            Request a new link
          </button>
        </>
      );
    }

    return (
      <>
        <p className={styles.tagline}>Choose a new password</p>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* Hidden username field: without it, password managers have nothing to attach the
              new credential to and will often save it against the wrong entry or not at all. */}
          <input type="text" name="username" autoComplete="username" hidden readOnly value="" />

          <label className={styles.label}>
            New password
            <input
              className={styles.input}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoFocus
              autoComplete="new-password"
              minLength={MIN_PASSWORD}
              disabled={loading}
            />
          </label>
          <p className={styles.hint}>At least {MIN_PASSWORD} characters.</p>

          <label className={styles.label}>
            Confirm new password
            <input
              className={styles.input}
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
              disabled={loading}
            />
          </label>

          {/* Live guidance while typing; `error` below is what the submit attempt said. */}
          {tooShort && <div className={styles.error}>Password must be at least {MIN_PASSWORD} characters.</div>}
          {!tooShort && mismatch && <div className={styles.error}>Passwords do not match.</div>}
          {error && <div className={styles.error} role="alert">{error}</div>}

          <button
            className={styles.btn}
            type="submit"
            disabled={loading || password.length < MIN_PASSWORD || password !== confirm}
          >
            {loading ? 'Updating…' : 'Set new password'}
          </button>
        </form>

        <p className={styles.toggle}>
          Changed your mind?{' '}
          <button className={styles.link} onClick={() => navigate('/login')}>
            Back to sign in
          </button>
        </p>
      </>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>

        <button className={styles.logo} onClick={() => navigate('/')} aria-label="RepBuddy home">
          {logoOk ? (
            <img
              className={styles.logoImg}
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="RepBuddy"
              onError={() => setLogoOk(false)}
            />
          ) : (
            <div className={styles.logoMark}>R</div>
          )}
          <span className={styles.logoName}>RepBuddy</span>
        </button>

        {renderBody()}

        <button className={styles.backHome} onClick={() => navigate('/')}>
          ⬅️ Back to home
        </button>

      </div>
    </div>
  );
}
